import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { useProductLevelRequirementsProgress } from '../hooks/useProductLevelRequirementsProgress'
import { V2BriefingGuidanceHelpTrigger } from './V2BriefingGuidanceHelpTrigger'
import { V2RohsSharedEvidenceFileDialog } from './rohs-legislation-briefing/V2RohsSharedEvidenceFileDialog'
import { V2RohsSharedEvidenceFileItem } from './rohs-legislation-briefing/V2RohsSharedEvidenceFileItem'

type V2ProductManufacturerInformationCardProps = {
  productId: string
}

export function V2ProductManufacturerInformationCard({
  productId,
}: V2ProductManufacturerInformationCardProps) {
  const { t } = useTranslation('common')
  const canWrite = useCanWriteOrgData()
  const [dialogOpen, setDialogOpen] = useState(false)
  const {
    isLoading,
    hasManufacturerInformation,
    manufacturerFileName,
  } = useProductLevelRequirementsProgress(productId)

  if (isLoading) {
    return (
      <Card className='h-fit' aria-busy='true'>
        <CardHeader className='pb-3'>
          <Skeleton className='h-5 w-48' />
          <Skeleton className='mt-2 h-4 w-full max-w-sm' />
        </CardHeader>
        <CardContent>
          <Skeleton className='h-16 w-full' />
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card className='h-fit'>
        <CardHeader className='flex flex-row flex-wrap items-start justify-between gap-2 space-y-0 pb-3'>
          <div className='flex flex-wrap items-center gap-1.5'>
            <CardTitle className='text-base leading-tight'>
              {t(
                'v2.pages.products.detail.rohsBriefing.steps.items.manufacturerInformation.title'
              )}
            </CardTitle>
            <V2BriefingGuidanceHelpTrigger guidanceBase='v2.pages.products.detail.sharedEvidence.guidance.manufacturerInformation' />
          </div>
          {canWrite && !hasManufacturerInformation ? (
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => {
                setDialogOpen(true)
              }}
            >
              {t('v2.pages.products.detail.sharedEvidence.actions.upload')}
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className='space-y-3'>
          {manufacturerFileName != null ? (
            <V2RohsSharedEvidenceFileItem
              fileName={manufacturerFileName}
              canWrite={canWrite}
              onEdit={() => {
                setDialogOpen(true)
              }}
            />
          ) : (
            <p className='text-sm text-muted-foreground'>
              {t('v2.pages.products.detail.sharedEvidence.noFile')}
            </p>
          )}
        </CardContent>
      </Card>

      <V2RohsSharedEvidenceFileDialog
        productId={productId}
        slotKind='MANUFACTURER_INFORMATION'
        fileName={manufacturerFileName}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </>
  )
}
